import React, { useState, useEffect, useCallback } from 'react';
import { HelpCircle, RotateCcw, Clock, TrendingUp, Inbox, FileText, Bell, Trash2, Flag, AlertTriangle, Monitor } from 'lucide-react';
import { useAuth } from '../firebase/auth';
import {
  getAllPendingSubmissions,
  getPendingSubmissions,
  claimSubmission,
  unclaimSubmission,
  flagSubmissionForReview,
  getMarkerSubmissionCount
} from '../firebase/submissions';
import { getMarkerCaps, getHomeworkNames, sortHomeworkTypes } from '../firebase/settings';
import { getMarkerStats, resetMarkerStats, getMarkerOwnResetHistory, deleteResetLogEntry } from '../firebase/stats';
import FirebaseMarker from '../FirebaseMarker';
import FlagForReviewModal from './FlagForReviewModal';
import { useHistoryNavigation } from '../useHistoryNavigation';
import { DEFAULT_ORG_ID } from '../firebase/orgs';
import { getOrgBranding } from '../firebase/orgBranding';
import { getHeadMarkerName } from '../firebase/headMarkerNotifications';
import { getContrastTextColor } from '../utils/color';
import { goToCourseHosting } from '../firebase/crossApp';
import HelpPanel from './HelpPanel';
import useIsMobile from '../hooks/useIsMobile';

function MarkerDashboard() {
  const { user, logout } = useAuth();
  const [switchingToCourseHosting, setSwitchingToCourseHosting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const handleSwitchToCourseHosting = async () => {
    setSwitchingToCourseHosting(true);
    try {
      await goToCourseHosting();
    } catch (error) {
      alert(error.message || 'Could not switch to Course Content.');
      setSwitchingToCourseHosting(false);
    }
  };
  const isMobile = useIsMobile();
  const [currentView, setCurrentView] = useState('available'); // 'available', 'mysubmissions', 'tracking', or 'marking'
  const [availableSubmissions, setAvailableSubmissions] = useState([]); // All pending
  const [mySubmissions, setMySubmissions] = useState([]); // My claimed/marked
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [markerCaps, setMarkerCaps] = useState({});
  const [homeworkNames, setHomeworkNames] = useState({});
  const [selectedHomeworkFilter, setSelectedHomeworkFilter] = useState('all');
  const [myCounts, setMyCounts] = useState({});
  const [completedStats, setCompletedStats] = useState({});
  const [resetHistory, setResetHistory] = useState([]);
  const [showResetHistory, setShowResetHistory] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({});
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifLoaded, setNotifLoaded] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(null); // submissionId currently being flagged
  const [flagComment, setFlagComment] = useState('');
  const [orgLogoUrl, setOrgLogoUrl] = useState(null);
  const [orgHeaderColor, setOrgHeaderColor] = useState(null);
  const [headMarkerName, setHeadMarkerName] = useState('the head marker');

  const configuredHomeworkTypes = sortHomeworkTypes(Object.keys(homeworkNames));

  const isAileenOrg = user && (user.orgId || DEFAULT_ORG_ID) === DEFAULT_ORG_ID;

  useEffect(() => {
    if (!user || isAileenOrg) return;
    getOrgBranding(user.orgId || DEFAULT_ORG_ID).then(branding => {
      setOrgLogoUrl(branding?.logos?.marker?.url || branding?.logoUrl || null);
      setOrgHeaderColor(branding?.headerColors?.marker || null);
    });
  }, [user, isAileenOrg]);

  // Used in FlagForReviewModal's copy ("Explain the issue for <name>")
  // instead of a hardcoded tutor name, since the platform now has more
  // than one org.
  useEffect(() => {
    if (!user) return;
    getHeadMarkerName(user.orgId || DEFAULT_ORG_ID).then(setHeadMarkerName);
  }, [user]);

  // Browser back button navigation
  useHistoryNavigation(currentView, setCurrentView, 'available');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [available, mine, caps, names, stats, history] = await Promise.all([
        getAllPendingSubmissions(),
        getPendingSubmissions(user.uid), // Pass user.uid to get only this marker's claimed submissions
        getMarkerCaps(),
        getHomeworkNames(),
        getMarkerStats(user.uid),
        getMarkerOwnResetHistory(user.uid)
      ]);
      setAvailableSubmissions(available);
      setMySubmissions(mine);
      setMarkerCaps(caps);
      setHomeworkNames(names);
      setCompletedStats(stats);
      setResetHistory(history);
      
      // Count current submissions per homework type (parallel)
      const homeworkTypes = Object.keys(names);
      const countValues = await Promise.all(homeworkTypes.map(type => getMarkerSubmissionCount(user.uid, type)));
      const counts = Object.fromEntries(homeworkTypes.map((type, i) => [type, countValues[i]]));
      setMyCounts(counts);
      
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    // Only load when user is available
    if (user?.uid) {
      loadData();
    }
  }, [user, loadData]);

  useEffect(() => {
    if (currentView === 'notifications' && user?.email && !notifLoaded && Object.keys(homeworkNames).length > 0) {
      loadNotificationPrefs();
    }
  }, [currentView, user, notifLoaded, homeworkNames]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClaimSubmission = async (submission) => {
    const cap = markerCaps[submission.homeworkType];

    // Check cap locally using already-loaded counts — avoids an extra Firestore query
    if (cap !== null && cap !== undefined) {
      const currentCount = myCounts[submission.homeworkType] || 0;
      if (currentCount >= cap) {
        alert(`You have reached the maximum of ${cap} submissions for this homework type`);
        return;
      }
    }

    try {
      await claimSubmission(
        submission.id,
        user.uid,
        user.name,
        submission.homeworkType,
        null // Cap already checked above
      );

      // Head marker notification for this now fires server-side, from the
      // onSubmissionClaimed Firestore trigger in functions/index.js - not
      // from here. A client-side fire-and-forget call was unreliable if the
      // marker's tab/connection hiccupped right after claiming (the same
      // root cause as an earlier stale-notification investigation).

      // Optimistically update local state — no re-fetch needed
      setAvailableSubmissions(prev => prev.filter(s => s.id !== submission.id));
      setMySubmissions(prev => [...prev, { ...submission, status: 'marking', assignedTo: user.uid, markerName: user.name }]);
      setMyCounts(prev => ({ ...prev, [submission.homeworkType]: (prev[submission.homeworkType] || 0) + 1 }));
      
    } catch (error) {
      console.error('Claim error:', error);
      alert(error.message || 'Failed to claim submission');
    }
  };

  const handleMarkNow = async (submission) => {
    // If rejected, set back to marking status before opening
    if (submission.status === 'rejected') {
      try {
        const { updateSubmissionStatus } = await import('../firebase/submissions');
        await updateSubmissionStatus(submission.id, 'marking');
        submission = { ...submission, status: 'marking' };
      } catch (e) {
        console.error('Failed to reset submission status:', e);
      }
    }
    setSelectedSubmission(submission);
    setCurrentView('marking');
  };

  const handleUnclaimSubmission = async (submission) => {
    if (!window.confirm(`Return this submission (${submission.homeworkType} — ${submission.studentName}) to the available pool?`)) return;
    try {
      await unclaimSubmission(submission.id);
      setMySubmissions(prev => prev.filter(s => s.id !== submission.id));
      setAvailableSubmissions(prev => [{ ...submission, status: 'pending', assignedTo: null, markerName: null }, ...prev]);
      setMyCounts(prev => ({ ...prev, [submission.homeworkType]: Math.max(0, (prev[submission.homeworkType] || 0) - 1) }));
    } catch (error) {
      console.error('Unclaim error:', error);
      alert('Failed to unclaim submission. Please try again.');
    }
  };

  const handleFlagSubmission = async (submissionId) => {
    if (!flagComment.trim()) return;
    const submission = mySubmissions.find(s => s.id === submissionId);

    try {
      await flagSubmissionForReview(submissionId, user.uid, user.name, flagComment);

      // Notify head marker (non-blocking)
      try {
        const { sendHeadMarkerNotification, NOTIFICATION_TYPES } = await import('../firebase/headMarkerNotifications');
        sendHeadMarkerNotification(NOTIFICATION_TYPES.SUBMISSION_FLAGGED_FOR_REVIEW, {
          studentName: submission?.studentName,
          homeworkType: homeworkNames[submission?.homeworkType] || submission?.homeworkType,
          markerName: user.name,
          flagComment
        });
      } catch (e) { console.error('Notification error:', e); }

      setMySubmissions(prev => prev.filter(s => s.id !== submissionId));
      setShowFlagModal(null);
      setFlagComment('');
    } catch (error) {
      console.error('Flag submission error:', error);
      alert('Failed to flag submission. Please try again.');
    }
  };

  const handleResetStats = async () => {
    const confirmReset = window.confirm(
      'Are you sure you want to reset your homework tracking? This will save your current counts to history and start fresh.'
    );
    
    if (!confirmReset) return;
    
    try {
      await resetMarkerStats(user.uid);
      
      // Notify head marker (non-blocking)
      try {
        const { sendHeadMarkerNotification, NOTIFICATION_TYPES } = await import('../firebase/headMarkerNotifications');
        sendHeadMarkerNotification(NOTIFICATION_TYPES.MARKER_STAT_RESET, {
          markerName: user.name,
          resetAt: new Date().toLocaleString('en-GB')
        });
      } catch (e) { console.error('Notification error:', e); }
      
      await loadData(); // Reload to get updated stats
      alert('Tracking reset successfully. Your previous counts have been saved to history.');
    } catch (error) {
      console.error('Reset stats error:', error);
      alert('Failed to reset tracking. Please try again.');
    }
  };

  const handleDeleteResetEntry = async (entryId) => {
    if (!window.confirm('Delete this reset history entry? This cannot be undone.')) return;

    try {
      await deleteResetLogEntry(user.uid, entryId);
      setResetHistory(prev => prev.filter(record => record.id !== entryId));
    } catch (error) {
      console.error('Delete reset entry error:', error);
      alert('Failed to delete entry. Please try again.');
    }
  };

  const loadNotificationPrefs = async () => {
    if (!user?.email) return;
    setNotifLoading(true);
    try {
      const { getDoc, doc } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');
      const types = Object.keys(homeworkNames);
      // Per-org since 2026-09-28 (orgs/{orgId}/notificationSettings) - read
      // by the onSubmissionCreated email function. Aileen's org falls back
      // to the old shared doc until a per-org copy exists.
      const orgId = user.orgId || DEFAULT_ORG_ID;
      const snapshots = await Promise.all(types.map(async (hwType) => {
        const snap = await getDoc(doc(db, 'orgs', orgId, 'notificationSettings', hwType));
        return (!snap.exists() && orgId === DEFAULT_ORG_ID) ? getDoc(doc(db, 'notificationSettings', hwType)) : snap;
      }));
      const prefs = Object.fromEntries(types.map((hwType, i) => [hwType, (snapshots[i].data()?.markerEmails || []).includes(user.email)]));
      setNotificationPrefs(prefs);
      setNotifLoaded(true);
    } catch (error) {
      console.error('Failed to load notification prefs:', error);
    } finally {
      setNotifLoading(false);
    }
  };

  const toggleNotification = async (hwType) => {
    if (!user?.email) return;
    try {
      const { getDoc, setDoc, doc } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');
      const orgId = user.orgId || DEFAULT_ORG_ID;
      const docRef = doc(db, 'orgs', orgId, 'notificationSettings', hwType);
      let docSnap = await getDoc(docRef);
      if (!docSnap.exists() && orgId === DEFAULT_ORG_ID) docSnap = await getDoc(doc(db, 'notificationSettings', hwType));
      const currentEmails = docSnap.data()?.markerEmails || [];
      let updatedEmails;
      if (currentEmails.includes(user.email)) {
        updatedEmails = currentEmails.filter(e => e !== user.email);
      } else {
        updatedEmails = [...currentEmails, user.email];
      }
      await setDoc(docRef, { markerEmails: updatedEmails }, { merge: true });
      // Aileen's org: keep the old shared doc in step - the Google Sheet
      // script still reads it
      if (orgId === DEFAULT_ORG_ID) {
        await setDoc(doc(db, 'notificationSettings', hwType), { markerEmails: updatedEmails }, { merge: true });
      }
      setNotificationPrefs(prev => ({ ...prev, [hwType]: !prev[hwType] }));
    } catch (error) {
      console.error('Failed to toggle notification:', error);
      alert('Failed to update notification preference. Please try again.');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFilteredSubmissions = (submissions) => {
    if (selectedHomeworkFilter === 'all') {
      return submissions;
    }
    return submissions.filter(s => s.homeworkType === selectedHomeworkFilter);
  };

  if (loading) {
    return (
      <div style={{
        fontFamily: "'Crimson Pro', 'Georgia', serif",
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f1e8 0%, #e8e0d5 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '40px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <h2>Loading submissions...</h2>
        </div>
      </div>
    );
  }

  const headerBg = orgHeaderColor || '#2c3e50';

  return (
    <div style={{
      fontFamily: "'Crimson Pro', 'Georgia', serif",
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f1e8 0%, #e8e0d5 100%)',
      padding: isMobile ? '8px' : '20px'
    }}>
      {currentView === 'marking' && isMobile ? (
        // The marking screen needs a computer (precise annotation on the
        // PDF, Chrome only) - on a phone, say so rather than show it broken
        <div style={{ background: 'white', borderRadius: '12px', padding: '28px 20px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <Monitor size={40} color="#c33329" style={{ marginBottom: '12px' }} />
          <h2 style={{ margin: '0 0 8px 0', fontSize: '20px' }}>Marking needs a computer</h2>
          <p style={{ margin: '0 0 20px 0', color: '#4a5568', fontSize: '15px' }}>
            Please open this submission in Chrome on a laptop or desktop to mark it.
          </p>
          <button
            onClick={() => { setCurrentView('mysubmissions'); setSelectedSubmission(null); }}
            style={{ padding: '10px 20px', background: '#c33329', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
          >
            Back to My Submissions
          </button>
        </div>
      ) : currentView === 'marking' ? (
        <FirebaseMarker
          submission={selectedSubmission}
          onBack={async () => {
            setCurrentView('mysubmissions');
            setSelectedSubmission(null);
            const mine = await getPendingSubmissions(user.uid);
            setMySubmissions(mine);
          }}
          user={user}
        />
      ) : (
        <>
          {/* Thin Header Bar */}
          <div style={{
            background: headerBg,
            color: getContrastTextColor(headerBg),
            padding: isMobile ? '10px 12px' : '12px 24px',
            borderRadius: '12px 12px 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '10px'
          }}>
            {/* Logo */}
            {isAileenOrg ? (
              <img
                src="/aileen-edgar-logo-dark.png"
                alt="Aileen Edgar"
                style={{ height: isMobile ? '34px' : '50px', width: 'auto', maxWidth: isMobile ? '40vw' : 'none', objectFit: 'contain' }}
              />
            ) : orgLogoUrl ? (
              <img
                src={orgLogoUrl}
                alt="Logo"
                style={{ height: isMobile ? '34px' : '50px', width: 'auto', maxWidth: isMobile ? '40vw' : 'none', objectFit: 'contain' }}
              />
            ) : (
              <span style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '700' }}>
                Homework Solutions
              </span>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '10px', flexShrink: 0 }}>
            {/* Help / FAQs - see HelpPanel.jsx */}
            <button
              type="button"
              onClick={() => setShowHelp(true)}
              title="Help and frequently asked questions"
              style={{ padding: isMobile ? '8px 10px' : '12px 18px', background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: isMobile ? '13px' : undefined, display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <HelpCircle size={16} />
              {!isMobile && 'Help'}
            </button>
            {/* Course Content Button */}
            <button
              onClick={handleSwitchToCourseHosting}
              disabled={switchingToCourseHosting}
              style={{
                padding: isMobile ? '8px 10px' : '12px 24px',
                background: 'rgba(255,255,255,0.15)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: switchingToCourseHosting ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: isMobile ? '13px' : undefined
              }}
            >
              {switchingToCourseHosting ? 'Switching...' : (isMobile ? 'Course' : 'Course Content')}
            </button>

            {/* Logout Button */}
            <button
              onClick={logout}
              style={{
                padding: isMobile ? '8px 10px' : '12px 24px',
                background: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: isMobile ? '13px' : undefined
              }}
            >
              Logout
            </button>
            </div>
          </div>

          {/* Page Title Section */}
          <div style={{
            background: 'white',
            padding: isMobile ? '14px 16px' : '24px',
            borderRadius: '0 0 12px 12px',
            marginBottom: isMobile ? '12px' : '24px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}>
            <h1 style={{ margin: 0, fontSize: isMobile ? '22px' : '32px', color: '#2c3e50', fontWeight: '700' }}>
              Marker Dashboard
            </h1>
            <p style={{ margin: '8px 0 0 0', color: '#718096', fontSize: '16px' }}>
              Welcome back, {user?.name || user?.email}
            </p>
          </div>

          {/* Rejection Alert Banner */}
          {mySubmissions.some(s => s.status === 'rejected') && (
            <div style={{
              background: '#fff5f5',
              border: '2px solid #e74c3c',
              borderRadius: '12px',
              padding: isMobile ? '12px 14px' : '16px 20px',
              marginBottom: isMobile ? '12px' : '24px',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <AlertTriangle size={24} color="#e74c3c" style={{ flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: '700', color: '#c0392b', fontSize: '16px' }}>
                  {mySubmissions.filter(s => s.status === 'rejected').length === 1
                    ? '1 submission has been returned for revision'
                    : `${mySubmissions.filter(s => s.status === 'rejected').length} submissions have been returned for revision`}
                </div>
                <div style={{ color: '#7f8c8d', fontSize: '13px', marginTop: '2px' }}>
                  Check your My Submissions tab for the head marker's feedback.
                </div>
              </div>
              <button
                onClick={() => setCurrentView('mysubmissions')}
                style={{
                  marginLeft: 'auto', padding: '8px 16px', background: '#e74c3c',
                  color: 'white', border: 'none', borderRadius: '6px',
                  cursor: 'pointer', fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap'
                }}
              >
                View Now
              </button>
            </div>
          )}

          {/* Tabs */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: isMobile ? '8px' : '16px',
            marginBottom: isMobile ? '12px' : '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            display: 'flex',
            gap: isMobile ? '6px' : '12px',
            flexWrap: isMobile ? 'nowrap' : 'wrap',
            overflowX: isMobile ? 'auto' : 'visible',
            WebkitOverflowScrolling: 'touch'
          }}>
            <button
              onClick={() => setCurrentView('available')}
              style={{
                padding: isMobile ? '8px 12px' : '12px 24px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                background: currentView === 'available' ? '#c33329' : '#ecf0f1',
                color: currentView === 'available' ? 'white' : '#2c3e50',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Available ({availableSubmissions.length})
            </button>
            
            <button
              onClick={() => setCurrentView('mysubmissions')}
              style={{
                padding: isMobile ? '8px 12px' : '12px 24px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                background: currentView === 'mysubmissions' ? '#c33329' : '#ecf0f1',
                color: currentView === 'mysubmissions' ? 'white' : '#2c3e50',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              My Submissions ({mySubmissions.length})
            </button>
            
            <button
              onClick={() => setCurrentView('tracking')}
              style={{
                padding: isMobile ? '8px 12px' : '12px 24px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                background: currentView === 'tracking' ? '#1b998b' : '#ecf0f1',
                color: currentView === 'tracking' ? 'white' : '#2c3e50',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <TrendingUp size={18} />
              My Tracking
            </button>

            <button
              onClick={() => setCurrentView('notifications')}
              style={{
                padding: isMobile ? '8px 12px' : '12px 24px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                background: currentView === 'notifications' ? '#667eea' : '#ecf0f1',
                color: currentView === 'notifications' ? 'white' : '#2c3e50',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Bell size={18} />
              Notifications
            </button>
          </div>

          {/* Homework Filter - Only show for Available and My Submissions */}
          {(currentView === 'available' || currentView === 'mysubmissions') && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: isMobile ? '12px' : '16px',
              marginBottom: isMobile ? '12px' : '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <div style={{ marginBottom: '12px', fontWeight: '600', fontSize: '14px' }}>
                Filter by homework:
              </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setSelectedHomeworkFilter('all')}
                style={{
                  padding: '8px 16px',
                  background: selectedHomeworkFilter === 'all' ? '#c33329' : '#f8f9fa',
                  color: selectedHomeworkFilter === 'all' ? 'white' : '#2c3e50',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '13px'
                }}
              >
                All
              </button>
              {configuredHomeworkTypes.map(type => (
                <button
                  key={type}
                  onClick={() => setSelectedHomeworkFilter(type)}
                  style={{
                    padding: '8px 16px',
                    background: selectedHomeworkFilter === type ? '#c33329' : '#f8f9fa',
                    color: selectedHomeworkFilter === type ? 'white' : '#2c3e50',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '13px',
                    position: 'relative'
                  }}
                >
                  {homeworkNames[type] || type}
                  {markerCaps[type] && (
                    <span style={{
                      marginLeft: '8px',
                      padding: '2px 6px',
                      background: myCounts[type] >= markerCaps[type] ? '#e74c3c' : '#1b998b',
                      color: 'white',
                      borderRadius: '10px',
                      fontSize: '11px'
                    }}>
                      {myCounts[type] || 0}/{markerCaps[type]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
          )}

          {/* Available Submissions View */}
          {currentView === 'available' && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: isMobile ? '14px' : '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <h2 style={{ margin: '0 0 16px 0', fontSize: '20px' }}>
                Available Submissions
              </h2>
              
              {getFilteredSubmissions(availableSubmissions).length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  color: '#7f8c8d' 
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
                    <Inbox size={48} color="#bdc3c7" />
                  </div>
                  <div style={{ fontSize: '18px' }}>No available submissions</div>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {getFilteredSubmissions(availableSubmissions).map(submission => {
                    const cap = markerCaps[submission.homeworkType];
                    const count = myCounts[submission.homeworkType] || 0;
                    const atCap = cap && count >= cap;
                    
                    return (
                      <div
                        key={submission.id}
                        style={{
                          padding: '16px',
                          border: '2px solid #e2e8f0',
                          borderRadius: '8px',
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr auto',
                          gap: isMobile ? '8px' : '16px',
                          alignItems: 'center'
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: '700', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {homeworkNames[submission.homeworkType] || submission.homeworkType}
                            {submission.submittedVia === 'exam_pdf_fallback' && (
                              <span style={{
                                background: '#f1f5f9',
                                color: '#475569',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '700',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                <FileText size={12} />
                                PDF UPLOAD (NO EXAM SIM)
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                            {submission.studentName}
                            {submission.cohortName && (
                              <span style={{
                                marginLeft: '8px',
                                color: '#667eea',
                                fontWeight: '600'
                              }}>
                                • {submission.cohortName}
                              </span>
                            )}
                          </div>
                        </div>

                        <div>
                          <div style={{ fontSize: '13px', color: '#4a5568' }}>
                            Submitted: {formatDate(submission.submittedAt)}
                          </div>
                          {submission.fileCount > 1 && (
                            <div style={{ fontSize: '12px', color: '#c33329', marginTop: '4px' }}>
                              {submission.fileCount} files
                            </div>
                          )}
                        </div>

                        <div>
                          {cap && (
                            <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                              Your count: {count}/{cap}
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <button
                            onClick={() => handleClaimSubmission(submission)}
                            disabled={atCap}
                            style={{
                              padding: '10px 20px',
                              background: atCap ? '#bdc3c7' : '#1b998b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: atCap ? 'not-allowed' : 'pointer',
                              fontWeight: '600',
                              fontSize: '14px'
                            }}
                            title={atCap ? 'You have reached the maximum for this homework type' : 'Claim this submission'}
                          >
                            {atCap ? 'At Cap' : 'Claim'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* My Submissions View */}
          {currentView === 'mysubmissions' && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: isMobile ? '14px' : '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <h2 style={{ margin: '0 0 16px 0', fontSize: '20px' }}>
                My Submissions
              </h2>
              
              {getFilteredSubmissions(mySubmissions).length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px', 
                  color: '#7f8c8d' 
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
                    <FileText size={48} color="#bdc3c7" />
                  </div>
                  <div style={{ fontSize: '18px' }}>No claimed submissions yet</div>
                  <div style={{ fontSize: '14px', marginTop: '8px' }}>
                    Go to "Available" tab to claim submissions
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {getFilteredSubmissions(mySubmissions).map(submission => {
                    const isRejected = submission.status === 'rejected';
                    return (
                    <div
                      key={submission.id}
                      style={{
                        padding: '16px',
                        border: isRejected ? '2px solid #e74c3c' : '2px solid #e2e8f0',
                        borderRadius: '8px',
                        background: isRejected ? '#fff9f9' : 'white',
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr auto',
                        gap: isMobile ? '10px' : '16px',
                        alignItems: 'start'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {homeworkNames[submission.homeworkType] || submission.homeworkType}
                          {submission.submittedVia === 'exam_pdf_fallback' && (
                            <span style={{
                              background: '#f1f5f9',
                              color: '#475569',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '700',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <FileText size={12} />
                              PDF UPLOAD (NO EXAM SIM)
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                          {submission.studentName}
                          {submission.cohortName && (
                            <span style={{
                              marginLeft: '8px',
                              color: '#667eea',
                              fontWeight: '600'
                            }}>
                              • {submission.cohortName}
                            </span>
                          )}
                        </div>
                        {isRejected && submission.rejectionNotes && (
                          <div style={{
                            marginTop: '10px',
                            padding: '10px 12px',
                            background: '#fff0f0',
                            border: '1px solid #f5c6cb',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: '#721c24'
                          }}>
                            <div style={{ fontWeight: '700', marginBottom: '4px' }}>Head marker feedback:</div>
                            {submission.rejectionNotes}
                          </div>
                        )}
                      </div>

                      <div>
                        <div style={{ fontSize: '13px', color: '#4a5568' }}>
                          Submitted: {formatDate(submission.submittedAt)}
                        </div>
                        {submission.fileCount > 1 && (
                          <div style={{ fontSize: '12px', color: '#c33329', marginTop: '4px' }}>
                            {submission.fileCount} files
                          </div>
                        )}
                        <div style={{
                          fontSize: '12px',
                          color: isRejected ? '#e74c3c' : submission.status === 'marking' ? '#f39c12' : '#1b998b',
                          marginTop: '4px',
                          fontWeight: '600'
                        }}>
                          {isRejected ? 'Returned for Revision' : submission.status === 'marking' ? 'In Progress' : 'Awaiting Review'}
                        </div>
                      </div>

                      <div style={isMobile
                        ? { display: 'flex', flexWrap: 'wrap', gap: '8px' }
                        : { display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'stretch' }}>
                        <button
                          onClick={() => handleMarkNow(submission)}
                          style={{
                            padding: '10px 20px',
                            background: isRejected ? '#e74c3c' : '#c33329',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '14px'
                          }}
                        >
                          {isRejected ? 'Re-mark' : submission.status === 'marking' ? 'Mark Submission' : 'View'}
                        </button>
                        {submission.status === 'marking' && (
                          <button
                            onClick={() => handleUnclaimSubmission(submission)}
                            style={{
                              padding: '8px 20px',
                              background: 'white',
                              color: '#718096',
                              border: '2px solid #e2e8f0',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '13px'
                            }}
                          >
                            Unclaim
                          </button>
                        )}
                        {submission.status === 'marking' && (
                          <button
                            onClick={() => { setShowFlagModal(submission.id); setFlagComment(''); }}
                            style={{
                              padding: '8px 20px',
                              background: 'white',
                              color: '#f39c12',
                              border: '2px solid #f39c12',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '13px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}
                          >
                            <Flag size={14} />
                            Flag for Review
                          </button>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Tracking View */}
          {currentView === 'tracking' && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: isMobile ? '14px' : '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
                marginBottom: '24px'
              }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                  Completed Homework Tracking
                </h2>
                <button
                  onClick={handleResetStats}
                  style={{
                    padding: '10px 20px',
                    background: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <RotateCcw size={16} />
                  Reset Tracking
                </button>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '12px',
                marginBottom: '16px'
              }}>
                {configuredHomeworkTypes.map(type => (
                  <div
                    key={type}
                    style={{
                      padding: '16px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: '14px', color: '#718096', marginBottom: '8px', fontWeight: '600' }}>
                      {homeworkNames[type] || type}
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#1b998b' }}>
                      {completedStats[type] || 0}
                    </div>
                    <div style={{ fontSize: '12px', color: '#a0aec0', marginTop: '4px' }}>
                      submitted
                    </div>
                  </div>
                ))}
              </div>

              {completedStats.lastReset && (
                <div style={{
                  fontSize: '13px',
                  color: '#718096',
                  marginTop: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '12px',
                  background: '#f8f9fa',
                  borderRadius: '6px'
                }}>
                  <Clock size={14} />
                  Last reset: {formatDate(completedStats.lastReset)}
                </div>
              )}

              {resetHistory.length > 0 && (
                <div style={{ marginTop: '24px', borderTop: '1px solid #e2e8f0', paddingTop: '24px' }}>
                  <button
                    onClick={() => setShowResetHistory(!showResetHistory)}
                    style={{
                      padding: '8px 16px',
                      background: 'transparent',
                      color: '#1b998b',
                      border: '1px solid #1b998b',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '13px'
                    }}
                  >
                    {showResetHistory ? 'Hide' : 'Show'} Reset History ({resetHistory.length})
                  </button>

                  {showResetHistory && (
                    <div style={{ marginTop: '16px' }}>
                      {resetHistory.map((record, index) => (
                        <div
                          key={record.id}
                          style={{
                            padding: '12px',
                            background: '#f8f9fa',
                            borderRadius: '6px',
                            marginBottom: '8px',
                            border: '1px solid #e2e8f0'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <div style={{ fontSize: '12px', color: '#718096', fontWeight: '600' }}>
                              Reset #{resetHistory.length - index} - {formatDate(record.resetAt)}
                            </div>
                            <button
                              onClick={() => handleDeleteResetEntry(record.id)}
                              title="Delete this entry"
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#e74c3c',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '4px'
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? 'repeat(4, 1fr)' : `repeat(${configuredHomeworkTypes.length}, 1fr)`,
                            gap: '8px',
                            fontSize: '12px'
                          }}>
                            {configuredHomeworkTypes.map(type => (
                              <div key={type} style={{ textAlign: 'center' }}>
                                <div style={{ fontWeight: '600', color: '#4a5568' }}>{type}</div>
                                <div style={{ color: '#718096' }}>{record[type] || 0}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Notifications View */}
          {currentView === 'notifications' && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: isMobile ? '14px' : '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600' }}>
                Email Notifications
              </h2>
              <p style={{ margin: '0 0 24px 0', color: '#718096', fontSize: '14px' }}>
                Choose which homework types you'd like to receive email notifications for when a new submission is available to claim.
              </p>

              {notifLoading ? (
                <div style={{ color: '#718096', fontSize: '14px', padding: '20px 0' }}>
                  Loading notification preferences...
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {Object.keys(homeworkNames).sort((a, b) => {
                    const thMatch = /^TH(\d+)$/;
                    const rhMatch = /^RH(\d+)$/;
                    const aIsTH = thMatch.test(a), bIsTH = thMatch.test(b);
                    const aIsRH = rhMatch.test(a), bIsRH = rhMatch.test(b);
                    if (aIsTH && bIsTH) return parseInt(a.match(thMatch)[1]) - parseInt(b.match(thMatch)[1]);
                    if (aIsRH && bIsRH) return parseInt(a.match(rhMatch)[1]) - parseInt(b.match(rhMatch)[1]);
                    if (aIsTH && !bIsTH) return -1;
                    if (!aIsTH && bIsTH) return 1;
                    if (aIsRH && !bIsRH) return -1;
                    if (!aIsRH && bIsRH) return 1;
                    return a.localeCompare(b);
                  }).map(hwType => (
                    <div
                      key={hwType}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '14px 16px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        background: notificationPrefs[hwType] ? '#f0fdf4' : 'white'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '15px', color: '#2c3e50' }}>
                          {homeworkNames[hwType] || hwType}
                        </div>
                        <div style={{ fontSize: '12px', color: '#718096', marginTop: '2px' }}>
                          {notificationPrefs[hwType] ? 'Email notifications enabled' : 'Email notifications off'}
                        </div>
                      </div>
                      <button
                        onClick={() => toggleNotification(hwType)}
                        style={{
                          padding: '8px 20px',
                          background: notificationPrefs[hwType] ? '#1b998b' : '#e2e8f0',
                          color: notificationPrefs[hwType] ? 'white' : '#718096',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '13px'
                        }}
                      >
                        {notificationPrefs[hwType] ? 'On' : 'Off'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <FlagForReviewModal
        submissionId={showFlagModal}
        onClose={() => { setShowFlagModal(null); setFlagComment(''); }}
        flagComment={flagComment}
        setFlagComment={setFlagComment}
        onFlag={handleFlagSubmission}
        headMarkerName={headMarkerName}
      />
      {showHelp && <HelpPanel role="marker" tutorName={headMarkerName} onClose={() => setShowHelp(false)} />}
    </div>
  );
}

export default MarkerDashboard;